import { useState, useCallback } from 'react';
import PrintChecklist from './PrintChecklist';
import { SETS } from './cardData';
import { BASE_PARALLELS, INSERT_PARALLELS, AUTO_PARALLELS, parallelKey } from './parallelData';

const LIST_OPTIONS = [
  { id: 'wishlist', label: '★ Wishlist', color: 'var(--gold)' },
  { id: 'trade', label: '⇄ For Trade', color: 'var(--cyan)' },
  { id: 'for_sale', label: '$ For Sale', color: 'var(--purple)' },
  { id: 'graded', label: '🏆 Graded', color: 'var(--ice)' },
];

const SET_OPTIONS = [
  { id: 'base', label: 'Base Set' },
  { id: 'insert', label: 'Inserts' },
  { id: 'auto', label: 'Autographs' },
  { id: 'sketch', label: 'Sketch Artists' },
];

// All unique parallels across all set types
export default function ShareModal({ user, profile, cardStates, salePrices, gradings, setMeta, SETS, SET_PARALLELS = [], SET_AUTO_PARALLELS = [], SET_INSERT_PARALLELS = [], onClose }) {
  const [selectedLists, setSelectedLists] = useState(['wishlist', 'trade', 'for_sale']);
  const [selectedSets, setSelectedSets] = useState(['base', 'insert', 'auto']);
  const [selectedParallels, setSelectedParallels] = useState(SET_PARALLELS.map(p => p.id));
  const [includeBaseCards, setIncludeBaseCards] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  const toggleList = (id) => setSelectedLists(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const toggleSet = (id) => setSelectedSets(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const toggleParallel = (id) => setSelectedParallels(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );
  const toggleAllParallels = () => {
    if (selectedParallels.length === SET_PARALLELS.length) {
      setSelectedParallels([]);
    } else {
      setSelectedParallels(SET_PARALLELS.map(p => p.id));
    }
  };

  // Build the shareable URL
  const shareUrl = `${window.location.origin}/u/${profile.username}?setId=${setMeta.id}&lists=${selectedLists.join(',')}&sets=${selectedSets.join(',')}&parallels=${selectedParallels.join(',')}&basecards=${includeBaseCards ? '1' : '0'}`;

  // Get parallels for a set type filtered by selection
  const getFilteredParallels = (setType) => {
    const allForType = setType === 'auto' ? (SET_AUTO_PARALLELS.length > 0 ? SET_AUTO_PARALLELS : AUTO_PARALLELS) : setType === 'insert' ? (SET_INSERT_PARALLELS.length > 0 ? SET_INSERT_PARALLELS : INSERT_PARALLELS) : (SET_PARALLELS.length > 0 ? SET_PARALLELS : BASE_PARALLELS);
    return allForType.filter(p => selectedParallels.includes(p.id));
  };

  // Build the text list
  const buildTextList = useCallback(() => {
    const lines = [];
    lines.push(`🌟 ${profile.username}'s ${setMeta.name} Collection`);
    lines.push(`View full profile: ${window.location.origin}/u/${profile.username}`);
    lines.push('');

    selectedLists.forEach(listId => {
      const listLabel = LIST_OPTIONS.find(l => l.id === listId)?.label;
      const cards = [];

      SETS.filter(set => selectedSets.includes(set.type)).forEach(set => {
        const filteredParallels = getFilteredParallels(set.type);

        set.cards.forEach(card => {
          const key = `${setMeta.id}__${set.id}__${card.n}`;
          const state = cardStates[key] || null;
          const grading = gradings[key] || null;

          // Base card
          if (includeBaseCards) {
            const matches =
              (listId === 'wishlist' && state === 'wishlist') ||
              (listId === 'trade' && state === 'trade') ||
              (listId === 'for_sale' && state === 'for_sale') ||
              (listId === 'graded' && grading);

            if (matches) {
              let line = `#${card.n} ${card.name}`;
              if (listId === 'for_sale' && salePrices[key]) line += ` — $${Number(salePrices[key]).toFixed(2)}`;
              if (listId === 'graded' && grading) line += ` — ${grading.company} ${grading.grade}`;
              cards.push(line);
            }
          }

          // Parallels
          filteredParallels.forEach(parallel => {
            const pKey = parallelKey(`${setMeta.id}__${set.id}`, card.n, parallel.id);
            const pState = cardStates[pKey] || null;
            const pGrading = gradings[pKey] || null;

            const pMatches =
              (listId === 'wishlist' && pState === 'wishlist') ||
              (listId === 'trade' && pState === 'trade') ||
              (listId === 'for_sale' && pState === 'for_sale') ||
              (listId === 'graded' && pGrading);

            if (pMatches) {
              let line = `#${card.n} ${card.name} — ${parallel.name}`;
              if (listId === 'for_sale' && salePrices[pKey]) line += ` — $${Number(salePrices[pKey]).toFixed(2)}`;
              if (listId === 'graded' && pGrading) line += ` — ${pGrading.company} ${pGrading.grade}`;
              cards.push(line);
            }
          });
        });
      });

      if (cards.length > 0) {
        lines.push(`── ${listLabel} (${cards.length} cards) ──`);
        cards.forEach(c => lines.push(`• ${c}`));
        lines.push('');
      }
    });

    return lines.join('\n');
  }, [selectedLists, selectedSets, selectedParallels, includeBaseCards, cardStates, salePrices, gradings, profile.username]);

  const copyText = () => {
    navigator.clipboard.writeText(buildTextList());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <>
      {showPrint && setMeta && SETS && (
        <PrintChecklist
          profile={profile}
          setMeta={setMeta}
          SETS={SETS}
          SET_PARALLELS={SET_PARALLELS}
          cardStates={cardStates}
          gradings={gradings}
          salePrices={salePrices}
          onClose={() => setShowPrint(false)}
        />
      )}
      <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <div style={styles.title}>SHARE YOUR LISTS</div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.body}>
          {/* List selection */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>INCLUDE LISTS</div>
            <div style={styles.optionRow}>
              {LIST_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  style={{
                    ...styles.optionBtn,
                    borderColor: selectedLists.includes(opt.id) ? opt.color : 'var(--border)',
                    color: selectedLists.includes(opt.id) ? opt.color : 'var(--muted-2)',
                    background: selectedLists.includes(opt.id) ? `${opt.color}11` : 'transparent',
                  }}
                  onClick={() => toggleList(opt.id)}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Set selection */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>INCLUDE SETS</div>
            <div style={styles.optionRow}>
              {SET_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  style={{
                    ...styles.optionBtn,
                    borderColor: selectedSets.includes(opt.id) ? 'var(--cyan)' : 'var(--border)',
                    color: selectedSets.includes(opt.id) ? 'var(--cyan)' : 'var(--muted-2)',
                    background: selectedSets.includes(opt.id) ? 'rgba(var(--cyan-rgb),0.08)' : 'transparent',
                  }}
                  onClick={() => toggleSet(opt.id)}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Base cards toggle */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>BASE CARDS</div>
            <div style={styles.optionRow}>
              <button
                style={{
                  ...styles.optionBtn,
                  borderColor: includeBaseCards ? 'var(--green)' : 'var(--border)',
                  color: includeBaseCards ? 'var(--green)' : 'var(--muted-2)',
                  background: includeBaseCards ? 'rgba(var(--green-rgb),0.08)' : 'transparent',
                }}
                onClick={() => setIncludeBaseCards(v => !v)}
              >{includeBaseCards ? '✓ Include Base Cards' : 'Exclude Base Cards'}</button>
            </div>
          </div>

          {/* Parallel selection */}
          <div style={styles.section}>
            <div style={styles.sectionLabelRow}>
              <div style={styles.sectionLabel}>PARALLELS</div>
              <button style={styles.toggleAllBtn} onClick={toggleAllParallels}>
                {selectedParallels.length === SET_PARALLELS.length ? 'DESELECT ALL' : 'SELECT ALL'}
              </button>
            </div>
            <div style={styles.optionRow}>
              {SET_PARALLELS.map(p => (
                <button
                  key={p.id}
                  style={{
                    ...styles.optionBtn,
                    borderColor: selectedParallels.includes(p.id) ? 'var(--gold)' : 'var(--border)',
                    color: selectedParallels.includes(p.id) ? 'var(--gold)' : 'var(--muted-2)',
                    background: selectedParallels.includes(p.id) ? 'rgba(var(--gold-rgb),0.08)' : 'transparent',
                    fontSize: '10px',
                  }}
                  onClick={() => toggleParallel(p.id)}
                >{p.name}</button>
              ))}
            </div>
          </div>

          {/* Print PDF */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>CHECKLIST PDF</div>
            <button
              style={styles.printPdfBtn}
              onClick={() => setShowPrint(true)}
            >
              🖨️ Print / Save as PDF
            </button>
          </div>

          {/* Social share buttons */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>SHARE ON</div>
            <div style={styles.socialRow}>
              {/* Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank" rel="noreferrer"
                style={{ ...styles.socialBtn, background: '#1877f2', borderColor: '#1877f2' }}
              >
                <span style={styles.socialIcon}>f</span>
                <span>Facebook</span>
              </a>

              {/* WhatsApp */}
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent('Check out my Star Wars Chrome Galaxy checklist! ' + shareUrl)}`}
                target="_blank" rel="noreferrer"
                style={{ ...styles.socialBtn, background: '#25d366', borderColor: '#25d366' }}
              >
                <span style={styles.socialIcon}>💬</span>
                <span>WhatsApp</span>
              </a>

              {/* Discord - copy formatted message */}
              <button
                style={{ ...styles.socialBtn, background: '#5865f2', borderColor: '#5865f2', cursor: 'pointer' }}
                onClick={() => {
                  navigator.clipboard.writeText(`🌟 Check out my ${setMeta.name} collection!\n${shareUrl}`);
                  alert('Discord message copied! Paste it in any Discord channel.');
                }}
              >
                <span style={styles.socialIcon}>🎮</span>
                <span>Discord</span>
              </button>

              {/* Email */}
              <a
                href={`mailto:?subject=${encodeURIComponent(`My ${setMeta.name} Checklist`)}&body=${encodeURIComponent(`Check out my ${setMeta.name} collection!\n\n` + shareUrl)}`}
                style={{ ...styles.socialBtn, background: 'var(--muted-2)', borderColor: 'var(--muted-2)' }}
              >
                <span style={styles.socialIcon}>✉</span>
                <span>Email</span>
              </a>

              {/* Instagram - copy link */}
              <button
                style={{ ...styles.socialBtn, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', borderColor: '#dc2743', cursor: 'pointer' }}
                onClick={() => { navigator.clipboard.writeText(shareUrl); alert('Link copied! Paste it in your Instagram bio or DMs.'); }}
              >
                <span style={styles.socialIcon}>📷</span>
                <span>Instagram</span>
              </button>
            </div>
          </div>

          {/* Shareable link */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>SHAREABLE LINK</div>
            <div style={styles.linkRow}>
              <div style={styles.linkBox}>{shareUrl}</div>
              <button style={styles.copyBtn} onClick={copyLink}>
                {copiedLink ? '✓ COPIED!' : 'COPY LINK'}
              </button>
            </div>
          </div>

          {/* Text preview */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>COPY/PASTE TEXT LIST</div>
            <textarea
              style={styles.textPreview}
              value={buildTextList()}
              readOnly
              rows={8}
            />
            <button style={styles.copyTextBtn} onClick={copyText}>
              {copied ? '✓ COPIED!' : 'COPY TEXT LIST'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: '20px',
  },
  modal: {
    background: 'var(--panel-deep)',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    width: '100%', maxWidth: '580px',
    maxHeight: '90vh', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '20px 24px',
    borderBottom: '1px solid var(--border)',
    background: 'linear-gradient(135deg, rgba(var(--cyan-rgb),0.05), rgba(var(--brand-gold-rgb),0.05))',
    flexShrink: 0,
  },
  title: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '14px', letterSpacing: '3px', color: 'var(--cyan)',
  },
  closeBtn: {
    background: 'none', border: '1px solid var(--border)',
    color: 'var(--muted-2)', cursor: 'pointer', fontSize: '14px',
    width: '32px', height: '32px', borderRadius: '2px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  body: { padding: '20px 24px', overflowY: 'auto' },
  section: { marginBottom: '20px' },
  sectionLabelRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' },
  sectionLabel: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '9px', letterSpacing: '2px', color: 'var(--muted-2)',
  },
  toggleAllBtn: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '8px', letterSpacing: '1px',
    padding: '3px 8px', background: 'transparent',
    border: '1px solid var(--border)', color: 'var(--muted-2)',
    cursor: 'pointer', borderRadius: '2px',
  },
  optionRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  optionBtn: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px', padding: '6px 12px',
    border: '1px solid', borderRadius: '2px',
    cursor: 'pointer', transition: 'all 0.15s',
  },
  linkRow: { display: 'flex', gap: '8px', alignItems: 'stretch' },
  linkBox: {
    flex: 1, background: 'var(--panel)',
    border: '1px solid var(--border)', borderRadius: '2px',
    padding: '8px 12px',
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '10px', color: 'var(--muted-2)',
    wordBreak: 'break-all', lineHeight: 1.4,
  },
  copyBtn: {
    fontFamily: "'Orbitron', monospace",
    fontSize: '9px', letterSpacing: '1px',
    padding: '8px 14px', background: 'rgba(var(--cyan-rgb),0.08)',
    border: '1px solid var(--cyan)', color: 'var(--cyan)',
    cursor: 'pointer', borderRadius: '2px', flexShrink: 0,
  },
  textPreview: {
    width: '100%', background: 'var(--panel)',
    border: '1px solid var(--border)', borderRadius: '2px',
    color: 'var(--text)', fontFamily: "'Share Tech Mono', monospace",
    fontSize: '10px', padding: '10px 12px',
    resize: 'none', outline: 'none',
    boxSizing: 'border-box', lineHeight: 1.6,
    marginBottom: '8px',
  },
  socialRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  socialBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontFamily: "'Rajdhani', sans-serif", fontWeight: 600,
    fontSize: '13px', padding: '8px 14px',
    border: '1px solid', borderRadius: '4px',
    color: '#fff', textDecoration: 'none',
    cursor: 'pointer', transition: 'opacity 0.15s',
  },
  socialIcon: { fontSize: '14px', lineHeight: 1 },
  printPdfBtn: {
    width: '100%',
    fontFamily: "'Orbitron', monospace",
    fontSize: '11px', letterSpacing: '2px',
    padding: '12px',
    background: 'linear-gradient(135deg, rgba(var(--brand-gold-rgb),0.1), rgba(var(--cyan-rgb),0.05))',
    border: '1px solid var(--brand-gold)', color: 'var(--brand-gold)',
    cursor: 'pointer', borderRadius: '2px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  },
  copyTextBtn: {
    width: '100%',
    fontFamily: "'Orbitron', monospace",
    fontSize: '10px', letterSpacing: '2px',
    padding: '12px', background: 'rgba(var(--green-rgb),0.08)',
    border: '1px solid var(--green)', color: 'var(--green)',
    cursor: 'pointer', borderRadius: '2px',
  },
};
